import { useParams } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getAcademics } from "services/content";
import PageHero from "components/shared/PageHero";
import SectionLabel from "components/shared/SectionLabel";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";

/**
 * Academics — a dedicated page per institution at /academics/<key>
 * (kcns | kcgs | honors), each with an intro and one or more titled
 * sub-sections of programme cards. The tab strip doubles as in-page
 * navigation and mirrors the header's Academics dropdown; bare /academics
 * falls back to the first tab.
 *
 * `normalize()` keeps the page working against the PREVIOUS content shape
 * ({ school, college, facilities }) so an un-migrated `academics` blob still
 * renders instead of blanking the page. Remove it once every environment has
 * been migrated to the tabbed shape.
 */
function normalize(data) {
  if (!data) return null;
  if (Array.isArray(data.tabs)) return data;

  const sections = [
    { title: "School — Class VI–X / SSC", groups: data.school || [] },
    { title: "College — HSC & Honours", groups: data.college || [] },
  ].filter((s) => s.groups.length);

  return {
    hero: data.hero,
    facilities: data.facilities || [],
    tabs: sections.length
      ? [
          {
            key: "kcgs",
            label: "KCGS",
            kicker: "School & College Section",
            color: "#8B1A4A",
            intro: "",
            sections,
          },
        ]
      : [],
  };
}

function GroupCard({ group }) {
  const color = group.color || "#005C40";
  const list = group.subs || [];
  const isList = group.variant === "list";

  return (
    <div className="bg-white border border-line rounded-2xl overflow-hidden shadow-[0_4px_18px_rgba(0,0,0,0.06)] transition-transform duration-300 hover:-translate-y-1">
      <div
        className="px-6 py-[22px] flex items-center gap-3.5"
        style={{ background: color }}
      >
        {group.icon && <span className="text-[30px]">{group.icon}</span>}
        <div className="font-serif text-xl font-bold text-white">{group.name}</div>
      </div>

      {isList ? (
        // Wider "programmes offered" card — one divided row per entry.
        <div className="flex flex-col">
          {list.map((s) => (
            <div
              key={s}
              className="flex items-center gap-2.5 px-6 py-3.5 border-b border-line last:border-b-0 text-sm font-semibold"
              style={{ color }}
            >
              <span
                className="w-[7px] h-[7px] rounded-full shrink-0"
                style={{ background: color }}
              />
              {s}
            </div>
          ))}
        </div>
      ) : (
        <div className="px-6 py-5">
          <div className="font-semibold text-xs text-muted tracking-[0.08em] uppercase mb-3">
            Subjects Offered
          </div>
          <div className="flex flex-col gap-2">
            {list.map((s) => (
              <div key={s} className="flex items-center gap-2.5 text-sm">
                <span
                  className="w-[7px] h-[7px] rounded-full shrink-0"
                  style={{ background: color }}
                />
                {s}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AcademicsPage() {
  const { data: raw } = useResource(getAcademics);
  const { inst } = useParams();
  const data = normalize(raw);
  const tabs = data?.tabs || [];

  // The URL segment picks the institution; an unknown or missing one falls
  // back to the first tab rather than rendering an empty page.
  const active = tabs.find((t) => t.key === inst) || tabs[0];
  const facilities = data?.facilities || [];

  return (
    <div className="animate-fadeIn">
      <PageHero
        title={data?.hero?.title || "Academics"}
        sub={
          data?.hero?.sub ||
          "One campus, three institutions — from a child's first classroom to an honours degree."
        }
        page={active ? `Academics › ${active.label}` : "Academics"}
        bg={
          active?.color
            ? `linear-gradient(140deg,#003d2b,${active.color})`
            : "linear-gradient(140deg,#003d2b,#005C40)"
        }
      />
      <section className="max-w-container mx-auto px-4 sm:px-10 py-[68px]">
        {/* Which institution this page covers. Switching happens in the header's
            Academics dropdown — the page itself shows one institution only. */}
        {active && (
          <div className="mb-8 pb-5 border-b border-line">
            <h2
              className="font-serif text-[26px] sm:text-[32px] font-bold leading-tight"
              style={{ color: active.color || "#005C40" }}
            >
              {active.label}
            </h2>
            {active.kicker && (
              <div className="text-[13px] text-muted mt-1">{active.kicker}</div>
            )}
          </div>
        )}

        {!data ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[320px] rounded-2xl" />
            ))}
          </div>
        ) : (
          <div key={active?.key} className="mb-14 animate-fadeIn">
            {active?.intro && (
              <p className="text-muted text-[14.5px] leading-[1.8] max-w-[620px] mb-8">
                {active.intro}
              </p>
            )}

            {(active?.sections || []).map((sec, si) => {
              const groups = sec.groups || [];
              // A lone card shouldn't stretch across the full row.
              const single = groups.length === 1;
              return (
                <div key={si} className={si ? "mt-10" : ""}>
                  {sec.title && (
                    <div className="font-semibold text-xs text-muted tracking-[0.1em] uppercase mb-4">
                      {sec.title}
                    </div>
                  )}
                  <div
                    className={
                      single
                        ? "grid grid-cols-1 md:max-w-[460px]"
                        : "grid grid-cols-1 md:grid-cols-3 gap-6"
                    }
                  >
                    {groups.map((g) => (
                      <GroupCard key={g.name} group={g} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Facilities */}
        <div>
          <SectionLabel label="Facilities & Infrastructure" />
          <h2 className="font-serif text-[32px] font-bold text-green-dark mb-7">
            Campus Facilities
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {!data
              ? Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-[132px] rounded-xl" />
                ))
              : facilities.map(({ icon, name, desc }, i) => (
                  <Reveal
                    key={name}
                    delay={(i % 4) * 80}
                    className="bg-white border border-line rounded-xl px-[18px] py-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="text-[30px] mb-2.5">{icon}</div>
                    <div className="font-bold text-[15px] text-green-dark mb-1.5">{name}</div>
                    <p className="text-[12.5px] text-muted leading-[1.6]">{desc}</p>
                  </Reveal>
                ))}
          </div>
        </div>
      </section>
    </div>
  );
}
